import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { markChequeCleared } from 'src/redux/slices/cheque-factoring';

import FormProvider from 'src/components/hook-form';

import { IChequeItem } from 'src/types/cheque';

interface IMarkClearedDialog {
  cheque: IChequeItem;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
}

function MarkClearedDialog({ cheque, isOpen, onClose, onSuccess }: IMarkClearedDialog) {
  const dispatch = useAppDispatcher();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        clearedDate: Yup.date().required('Cleared date is required'),
      }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: { clearedDate: new Date() },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await dispatch(
        markChequeCleared({
          chequeId: cheque._id,
          clearedDate: new Date(data.clearedDate),
        })
      ).unwrap();

      enqueueSnackbar('Cheque marked as cleared');
      reset();
      onSuccess();
      onClose();
    } catch (e) {
      if (typeof e === 'string') {
        enqueueSnackbar(e, { variant: 'error' });
      } else {
        enqueueSnackbar(e?.message || 'Failed to mark cheque as cleared', {
          variant: 'error',
        });
      }
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>Mark Cleared</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Confirm the date the remaining {fCurrencyRupees(cheque.amountInCents * 0.2)} (20%
            balance) was received for cheque <strong>{cheque.chequeNumber}</strong>.
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="clearedDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Cleared Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText: error?.message,
                    },
                  }}
                />
              )}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            Confirm
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default MarkClearedDialog;
